import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Notiflix from 'notiflix';
import { useAuth, BeneficiaryUser, UserCommon } from './auth';
import cardSharingService, {
  CardSharingItem,
  ProcessedCardSharingItem,
  InsuredWallet,
  CreateSharingResponse,
} from '../services/cardSharingService';
import {
  SharedCard,
  SharedPerson,
  CardSharingState,
  CreateInsuredSharingRequest,
} from '../pages/Wallet/SharedCards/types';
import api from '../services/api/api';

interface UseCardSharingReturn {
  sharedCards: SharedCard[];
  availableWallets: InsuredWallet[];
  loading: boolean;
  error: string | null;

  fetchSharedCards: (beneficiaryId?: string) => Promise<void>;
  fetchAvailableWallets: () => Promise<void>;
  createInsuredSharing: (
    request: CreateInsuredSharingRequest,
  ) => Promise<CreateSharingResponse>;
  deleteSharing: (token: string) => Promise<void>;
  refreshData: (beneficiaryId?: string) => Promise<void>;
  clearSharedCards: () => void;
  clearError: () => void;
}

const isBeneficiaryUser = (user: any): user is UserCommon & BeneficiaryUser => {
  return user && user.profile === 1 && user.userUnimedId;
};

const transformApiDataToSharedCards = (
  apiSharings: CardSharingItem[] | null,
): SharedCard[] => {
  if (!apiSharings || apiSharings.length === 0) {
    return [];
  }

  const processedSharings: ProcessedCardSharingItem[] = apiSharings.map(
    (sharing) => ({
      sharingId: sharing.token,
      token: sharing.token,
      insuredName: sharing.name || `Usuário ${sharing.cpf}`,
      sharingDate: sharing.startDate,
      status: sharing.sharingType === 'EXTERNO' ? 'Ativo' : 'Ativo',
    }),
  );

  const cardGroups = processedSharings.reduce<
    Record<string, ProcessedCardSharingItem[]>
  >((groups, sharing) => {
    const key = sharing.insuredName;
    if (!groups[key]) {
      // eslint-disable-next-line no-param-reassign
      groups[key] = [];
    }
    groups[key].push(sharing);
    return groups;
  }, {});

  return Object.entries(cardGroups).map(([key, sharings], cardIndex) => {
    const firstSharing = sharings[0];

    const sharedWith: SharedPerson[] = sharings.map((sharing) => {
      const originalApiData = apiSharings.find(
        (item) => item.token === sharing.token,
      );

      const displayName = originalApiData?.name || 'Não informado';

      return {
        id: sharing.token,
        name: displayName,
        phone: originalApiData?.cpf || '',
        avatar: undefined,
      };
    });

    return {
      id: `card-${cardIndex}`,
      holderName: firstSharing.insuredName,
      cardNumber: `${firstSharing.token.substring(0, 8)}****`,
      sharedWith,
      apiData: firstSharing,
    } as SharedCard;
  });
};

export const useCardSharing = (
  selectedBeneficiaryId?: string,
): UseCardSharingReturn => {
  const { user } = useAuth();
  const initializedRef = useRef(false);
  const lastBeneficiaryIdRef = useRef<string | undefined>(undefined);

  const [state, setState] = useState<CardSharingState>({
    loading: false,
    error: null,
    sharedCards: [],
    availableWallets: [],
  });

  const fetchSharedCards = useCallback(
    async (beneficiaryId?: string) => {
      if (!isBeneficiaryUser(user)) {
        setState((prev) => ({
          ...prev,
          error: 'Usuário não autenticado ou não é beneficiário',
        }));
        return;
      }

      const targetBeneficiaryId = beneficiaryId || user.userUnimedId.toString();

      setState((prev) => {
        if (prev.loading) return prev;
        return { ...prev, loading: true, error: null };
      });

      try {
        const { data } = await api.get(
          `/company/product-change?userUnimedId=${targetBeneficiaryId}`,
        );

        if (
          !data.content ||
          !Array.isArray(data.content) ||
          data.content.length <= 0
        ) {
          Notiflix.Notify.failure('Falha em obter as informações do seu plano');
          setState((prev) => ({
            ...prev,
            loading: false,
            sharedCards: [],
          }));
          return;
        }

        const userPlanCode = data.content[0].userPlanId;
        const apiSharings = await cardSharingService.getCardSharingList(
          userPlanCode,
        );

        const sharedCards = transformApiDataToSharedCards(apiSharings);

        setState((prev) => ({
          ...prev,
          sharedCards,
          loading: false,
        }));
      } catch (error) {
        console.error('Erro ao buscar compartilhamentos:', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Erro ao buscar compartilhamentos',
        }));
      }
    },
    [user?.id],
  );

  const fetchAvailableWallets = useCallback(async () => {
    if (!isBeneficiaryUser(user)) {
      setState((prev) => ({
        ...prev,
        error: 'Usuário não autenticado ou não é beneficiário',
      }));
      return;
    }

    setState((prev) => {
      if (prev.loading) return prev;
      return { ...prev, loading: true, error: null };
    });

    try {
      const wallets = await cardSharingService.getInsuredWallets(
        user.userUnimedId.toString(),
      );

      setState((prev) => ({
        ...prev,
        availableWallets: wallets || [],
        loading: false,
      }));
    } catch (error) {
      console.error('Erro ao buscar carteiras disponíveis:', error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar carteiras disponíveis',
      }));
    }
  }, [user?.id]);

  const createInsuredSharing = useCallback(
    async (
      request: CreateInsuredSharingRequest,
    ): Promise<CreateSharingResponse> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await cardSharingService.generateInsuredSharing(
          request.userUnimedId,
          request.sharedUserUnimedId,
        );

        setState((prev) => ({ ...prev, loading: false }));

        await fetchSharedCards();

        return response;
      } catch (error) {
        console.error('Erro ao criar compartilhamento entre segurados:', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Erro ao criar compartilhamento',
        }));
        throw error;
      }
    },
    [fetchSharedCards],
  );

  const deleteSharing = useCallback(
    async (token: string) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        await cardSharingService.deleteSharing(token);

        setState((prev) => ({
          ...prev,
          loading: false,
          sharedCards: prev.sharedCards
            .map((card) => ({
              ...card,
              sharedWith: card.sharedWith.filter(
                (person) => person.id !== token,
              ),
            }))
            .filter((card) => card.sharedWith.length > 0),
        }));

        await fetchSharedCards();
      } catch (error) {
        console.error('Erro ao excluir compartilhamento:', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Erro ao excluir compartilhamento',
        }));
        throw error;
      }
    },
    [fetchSharedCards],
  );

  const refreshData = useCallback(
    async (beneficiaryId?: string) => {
      if (!isBeneficiaryUser(user)) {
        return;
      }

      const targetBeneficiaryId = beneficiaryId || user.userUnimedId.toString();

      setState((prev) => {
        if (prev.loading) return prev;
        return { ...prev, loading: true, error: null };
      });

      try {
        const { data } = await api.get(
          `/company/product-change?userUnimedId=${targetBeneficiaryId}`,
        );

        if (
          !data.content ||
          !Array.isArray(data.content) ||
          data.content.length <= 0
        ) {
          Notiflix.Notify.failure('Falha em obter as informações do seu plano');
          setState((prev) => ({
            ...prev,
            loading: false,
            sharedCards: [],
            availableWallets: [],
          }));
          return;
        }

        const userPlanCode = data.content[0].userPlanId;
        const [apiSharings, wallets] = await Promise.all([
          cardSharingService.getCardSharingList(userPlanCode),
          cardSharingService.getInsuredWallets(targetBeneficiaryId),
        ]);

        const sharedCards = transformApiDataToSharedCards(apiSharings);

        setState((prev) => ({
          ...prev,
          sharedCards,
          availableWallets: wallets || [],
          loading: false,
        }));
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: 'Erro ao buscar dados',
        }));
      }
    },
    [user?.id],
  );

  const clearSharedCards = useCallback(() => {
    setState((prev) => ({ ...prev, sharedCards: [], error: null }));
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  useEffect(() => {
    if (isBeneficiaryUser(user)) {
      if (
        selectedBeneficiaryId &&
        selectedBeneficiaryId !== lastBeneficiaryIdRef.current
      ) {
        lastBeneficiaryIdRef.current = selectedBeneficiaryId;
        clearSharedCards();
        fetchSharedCards(selectedBeneficiaryId).catch((error) => {
          console.error(
            'Erro ao carregar compartilhamentos para beneficiário:',
            selectedBeneficiaryId,
            error,
          );
        });
      } else if (!selectedBeneficiaryId && !initializedRef.current) {
        initializedRef.current = true;
        fetchSharedCards().catch((error) => {
          console.error('Erro ao carregar compartilhamentos iniciais:', error);
        });
      }
    }
  }, [user?.id, selectedBeneficiaryId, fetchSharedCards, clearSharedCards]);

  const returnValue = useMemo<UseCardSharingReturn>(
    () => ({
      sharedCards: state.sharedCards,
      availableWallets: state.availableWallets,
      loading: state.loading,
      error: state.error,
      fetchSharedCards,
      fetchAvailableWallets,
      createInsuredSharing,
      deleteSharing,
      refreshData,
      clearSharedCards,
      clearError,
    }),
    [
      state.sharedCards,
      state.availableWallets,
      state.loading,
      state.error,
      fetchSharedCards,
      fetchAvailableWallets,
      createInsuredSharing,
      deleteSharing,
      refreshData,
      clearSharedCards,
      clearError,
    ],
  );

  return returnValue;
};
